import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  categories,
  confidences,
  defaultFilters,
  emptyFeed,
  matches,
  prune,
  receive,
  sources,
  type Feed,
  type Filters,
  type Post,
  type SourceStatus,
} from "./slopwatch-feed";
import "./slopwatch.css";
import { selectedExamples, type SelectedExample } from "./slopwatch-notable";

function countInteraction(
  event: "visible" | "filter" | "original" | "scanner" | "extension",
) {
  void fetch(`/api/slopwatch/interaction/${event}`, {
    method: "POST",
    credentials: "omit",
    keepalive: true,
    referrerPolicy: "no-referrer",
  }).catch(() => {});
}

const names = {
  bluesky: "Bluesky",
  reddit: "Reddit",
  mastodon: "Mastodon",
  lemmy: "Lemmy",
};
const categoryName = (name: string) =>
  name === "unavailable"
    ? "Category unavailable"
    : name[0].toUpperCase() + name.slice(1);
function statusName(status?: SourceStatus) {
  if (!status || status.state === "unavailable") return "Unavailable";
  if (status.state === "disconnected") return "Reconnecting";
  if (status.state === "paused")
    return status.reason === "customer_traffic"
      ? "Paused for customer scans"
      : "Collection paused";
  return "Running";
}
function ago(timestamp: string, now: number) {
  const seconds = Math.max(0, Math.floor((now - Date.parse(timestamp)) / 1000));
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
function useFeed() {
  const [feed, setFeed] = useState<Feed>(emptyFeed);
  const [connection, setConnection] = useState("connecting");
  const [now, setNow] = useState(Date.now);
  const ref = useRef(feed);
  useEffect(() => {
    if (typeof EventSource === "undefined") {
      setConnection("unavailable");
      return;
    }
    let stream: EventSource | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let disposed = false;
    let lastContact = Date.now();
    const publish = (next: Feed) => {
      ref.current = next;
      setFeed(next);
    };
    function connect() {
      if (disposed) return;
      lastContact = Date.now();
      const opened = new EventSource("/api/slopwatch/stream");
      stream = opened;
      opened.onerror = () => {
        if (!disposed && stream === opened) setConnection("reconnecting");
      };
      for (const kind of ["reset", "post", "remove", "status", "heartbeat"]) {
        opened.addEventListener(kind, (event) => {
          if (disposed || stream !== opened) return;
          try {
            const message = event as MessageEvent;
            if (message.data.length > 3 * 1024 * 1024)
              throw new Error("Feed event exceeds limit");
            publish(
              receive(
                ref.current,
                kind,
                JSON.parse(message.data),
                message.lastEventId,
                Date.now(),
              ),
            );
            lastContact = Date.now();
            setConnection("connected");
          } catch {
            opened.close();
            stream = undefined;
            setConnection("reconnecting");
            clearTimeout(retry);
            retry = setTimeout(connect, 5000);
          }
        });
      }
    }
    connect();
    const tick = () => {
      const time = Date.now();
      setNow(time);
      publish(prune(ref.current, time));
      if (time - lastContact > 45000) {
        lastContact = time;
        stream?.close();
        stream = undefined;
        setConnection("reconnecting");
        clearTimeout(retry);
        retry = setTimeout(connect, 5000);
      }
    };
    const interval = setInterval(tick, 1000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      disposed = true;
      clearTimeout(retry);
      clearInterval(interval);
      stream?.close();
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);
  return { feed, connection, now: now + feed.offset };
}

function Card({
  post,
  expanded,
  expand,
  now,
  absoluteDates = false,
}: {
  post: Post | undefined;
  expanded: boolean;
  expand: () => void;
  now: number;
  absoluteDates?: boolean;
}) {
  const element = useRef<HTMLElement>(null);
  const height = useRef(0);
  useLayoutEffect(() => {
    if (!post || !element.current) return;
    const measure = () => {
      height.current = element.current?.getBoundingClientRect().height ?? 0;
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element.current);
    return () => observer.disconnect();
  }, [post, expanded]);
  if (!post)
    return (
      <article
        ref={element}
        className="slopwatch-card slopwatch-removed"
        style={{ minHeight: height.current || undefined }}
      >
        <p>This post has expired or been removed.</p>
        <p>Resume live to see recent arrivals.</p>
      </article>
    );
  return (
    <article ref={element} className="slopwatch-card">
      <div className="slopwatch-card-meta">
        <span>{names[post.source]}</span>
        <span>{categoryName(post.category)}</span>
      </div>
      <h3>
        {post.label === "ai_generated" ? "AI-generated" : "AI-edited"}{" "}
        <span>{post.confidence} confidence</span>
      </h3>
      <p className={expanded ? "slopwatch-text expanded" : "slopwatch-text"}>
        {post.text}
      </p>
      <button
        type="button"
        className="slopwatch-read"
        aria-expanded={expanded}
        onClick={expand}
      >
        {expanded ? "Collapse text" : "Read full text"}
      </button>
      {absoluteDates && (
        <div className="slopwatch-times">
          <span>
            Posted{" "}
            <time
              dateTime={post.published_at}
              title={new Date(post.published_at).toLocaleString()}
            >
              {absoluteDates
                ? new Date(post.published_at).toLocaleString()
                : ago(post.published_at, now)}
            </time>
          </span>
          <span>
            Detected{" "}
            <time
              dateTime={post.detected_at}
              title={new Date(post.detected_at).toLocaleString()}
            >
              {absoluteDates
                ? new Date(post.detected_at).toLocaleString()
                : ago(post.detected_at, now)}
            </time>
          </span>
        </div>
      )}
      <a
        className="slopwatch-original"
        href={post.url}
        onClick={() => countInteraction("original")}
        target="_blank"
        rel="noopener noreferrer"
      >
        View original on {names[post.source]} <span aria-hidden="true">↗</span>
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    </article>
  );
}

export function SelectedExamples() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<SelectedExample[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState("loading");
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!open) {
      setItems([]);
      setSelected(null);
      setExpanded(false);
      return;
    }
    let disposed = false;
    let request: AbortController | undefined;
    let offset = 0;
    async function refresh() {
      request?.abort();
      const controller = new AbortController();
      request = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch("/api/v1/slopwatch/notable", {
          signal: controller.signal,
          cache: "no-store",
        });
        if (!response.ok) throw new Error("Selected examples unavailable");
        const body = await response.text();
        if (body.length > 1024 * 1024) throw new Error("Examples exceed limit");
        const value = selectedExamples(JSON.parse(body));
        if (disposed || controller.signal.aborted || request !== controller)
          return;
        offset = value.serverTime - Date.now();
        setNow(value.serverTime);
        setItems(value.examples);
        setSelected(
          (previous) => previous ?? value.examples[0]?.post.event_id ?? null,
        );
        setStatus("ready");
      } catch {
        if (!disposed && request === controller) {
          setItems([]);
          setStatus("error");
        }
      } finally {
        clearTimeout(timeout);
      }
    }
    setStatus("loading");
    void refresh();
    const tick = () => {
      const time = Date.now() + offset;
      setNow(time);
      setItems((previous) => {
        const retained = previous.filter(
          (item) => item.post.expires_at_unix * 1000 > time,
        );
        return retained.length === previous.length ? previous : retained;
      });
    };
    const expiry = setInterval(tick, 1000);
    const poll = setInterval(() => void refresh(), 60000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      disposed = true;
      request?.abort();
      clearInterval(expiry);
      clearInterval(poll);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [open]);
  const index = items.findIndex((item) => item.post.event_id === selected);
  const item = items[index];
  function choose(next: number) {
    setSelected(items[next]?.post.event_id ?? null);
    setExpanded(false);
  }
  return (
    <details
      className="slopwatch-selected"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>Selected examples</summary>
      {open && (
        <>
          <p>
            Manually selected posts, with their original publication and
            detection dates.
          </p>
          {status === "loading" && (
            <p role="status">Loading selected examples…</p>
          )}
          {status === "error" && (
            <p role="status">
              Selected examples are unavailable. We’ll try again shortly.
            </p>
          )}
          {status === "ready" && !items.length && (
            <p>No selected examples are available right now.</p>
          )}
          {items.length > 0 && (
            <>
              <div className="slopwatch-example-navigation">
                <button
                  type="button"
                  disabled={index <= 0}
                  onClick={() => choose(index - 1)}
                >
                  Previous example
                </button>
                <span>
                  {index >= 0
                    ? `${index + 1} of ${items.length}`
                    : "Example removed or expired"}
                </span>
                <button
                  type="button"
                  disabled={index === items.length - 1}
                  onClick={() => choose(index + 1)}
                >
                  Next example
                </button>
              </div>
              {item && (
                <div className="slopwatch-selected-layout">
                  <div className="slopwatch-example-context">
                    <h3>Why we selected it</h3>
                    <p>{item.context}</p>
                    <dl>
                      <dt>Selected</dt>
                      <dd>
                        <time dateTime={item.selected_at}>
                          {new Date(item.selected_at).toLocaleString()}
                        </time>
                      </dd>
                      <dt>Last reviewed</dt>
                      <dd>
                        <time dateTime={item.reviewed_at}>
                          {new Date(item.reviewed_at).toLocaleString()}
                        </time>
                      </dd>
                    </dl>
                  </div>
                  <Card
                    post={item.post}
                    absoluteDates
                    now={now}
                    expanded={expanded}
                    expand={() => setExpanded(!expanded)}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </details>
  );
}

export function SlopwatchPanel() {
  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!panel.current || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        countInteraction("visible");
        observer.disconnect();
      }
    });
    observer.observe(panel.current);
    return () => observer.disconnect();
  }, []);
  const { feed, connection, now } = useFeed();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [pause, setPause] = useState<{
    visible: string[];
    seen: string[];
  } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const interval = feed.arrivalRate
    ? Math.max(
        1,
        Math.round(
          feed.arrivalRate.seconds / Math.max(1, feed.arrivalRate.count),
        ),
      )
    : 0;
  const scanTotals = Object.values(feed.sources).reduce(
    (total, status) => {
      if (
        !status?.observed_at ||
        now + feed.offset - Date.parse(status.observed_at) > 45000
      )
        return total;
      return {
        scanned: total.scanned + (status.counters.window_scanned ?? 0),
        flagged: total.flagged + (status.counters.window_flagged ?? 0),
      };
    },
    { scanned: 0, flagged: 0 },
  );
  const percentage =
    connection === "connected" && scanTotals.scanned > 0
      ? ((100 * scanTotals.flagged) / scanTotals.scanned).toFixed(2)
      : null;
  const selected = feed.posts.filter((p) => matches(p, filters));
  const visible = pause
    ? pause.visible
    : selected.slice(0, 9).map((p) => p.event_id);
  const newCount = pause
    ? selected.filter((p) => !pause.seen.includes(p.event_id)).length
    : 0;
  const running = sources.filter(
    (source) => feed.sources[source]?.state === "running",
  ).length;
  const customerTraffic = sources.some(
    (source) =>
      feed.sources[source]?.state === "paused" &&
      feed.sources[source]?.reason === "customer_traffic",
  );
  const label =
    connection === "connected"
      ? running
        ? "Live"
        : customerTraffic
          ? "Collection paused due to heavy API usage — check back later!"
          : "Collection temporarily unavailable"
      : connection === "unavailable"
        ? "Live feed unavailable in this browser"
        : connection === "connecting"
          ? "Connecting…"
          : "Reconnecting to the live feed";
  const freeze = () =>
    setPause({ visible, seen: feed.posts.map((p) => p.event_id) });
  function filter<K extends keyof Filters>(key: K, value: Filters[K]) {
    countInteraction("filter");
    const next = { ...filters, [key]: value };
    setFilters(next);
    setExpanded(null);
    if (pause)
      setPause({
        visible: feed.posts
          .filter((p) => matches(p, next))
          .slice(0, 9)
          .map((p) => p.event_id),
        seen: feed.posts.map((p) => p.event_id),
      });
  }
  return (
    <section
      ref={panel}
      className="slopwatch"
      aria-labelledby="slopwatch-heading"
    >
      <header className="slopwatch-heading">
        <div>
          <h2 id="slopwatch-heading">Slopwatch</h2>
          <p>Public posts, flagged live by Stylome.</p>
        </div>
        <div
          className="slopwatch-live"
          data-live={connection === "connected" && running > 0}
        >
          <span aria-hidden="true" />
          {label}
        </div>
      </header>
      <div className="slopwatch-reading">
        <p className="slopwatch-rate">
          {feed.arrivalRate && feed.arrivalRate.count > 0 ? (
            <>
              A new piece of AI slop every{" "}
              <strong>
                {interval} {interval === 1 ? "second" : "seconds"}.
              </strong>
            </>
          ) : (
            "Watching for the next piece of AI slop…"
          )}
        </p>
        <div className="slopwatch-toolbar">
          <button
            type="button"
            className="slopwatch-settings-button"
            aria-label="Settings"
            title="Settings"
            aria-expanded={settingsOpen}
            aria-controls="slopwatch-settings"
            onClick={() => setSettingsOpen(!settingsOpen)}
          >
            <span aria-hidden="true">⚙</span>
          </button>
          <button
            type="button"
            className="slopwatch-playback"
            aria-label={pause ? "Resume live" : "Pause live feed"}
            title={pause ? "Resume live" : "Pause live feed"}
            onClick={() => {
              if (pause) {
                setPause(null);
                setExpanded(null);
              } else freeze();
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              {pause ? (
                <path d="M7 4v16l14-8z" />
              ) : (
                <path d="M6 4h4v16H6zm8 0h4v16h-4z" />
              )}
            </svg>
            {pause && newCount > 0 && (
              <span className="slopwatch-new-count">{newCount} new</span>
            )}
          </button>
        </div>
      </div>
      {settingsOpen && (
        <div id="slopwatch-settings" className="slopwatch-controls">
          <label>
            Source
            <select
              value={filters.source}
              onChange={(e) =>
                filter("source", e.target.value as Filters["source"])
              }
            >
              <option value="all">All sources</option>
              {sources.map((s) => (
                <option key={s} value={s}>
                  {names[s]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Category
            <select
              value={filters.category}
              onChange={(e) =>
                filter("category", e.target.value as Filters["category"])
              }
            >
              <option value="all">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {categoryName(c)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Classification
            <select
              value={filters.label}
              onChange={(e) =>
                filter("label", e.target.value as Filters["label"])
              }
            >
              <option value="ai_generated">AI-generated</option>
              <option value="ai_edited">AI-edited</option>
              <option value="all">Generated + edited</option>
            </select>
          </label>
          <label>
            Confidence
            <select
              value={filters.confidence}
              onChange={(e) =>
                filter("confidence", e.target.value as Filters["confidence"])
              }
            >
              {[...confidences].reverse().map((c) => (
                <option key={c} value={c}>
                  {c === "high"
                    ? "High"
                    : c === "medium"
                      ? "Medium or higher"
                      : "Show all"}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      <div className="slopwatch-cards">
        {visible.map((id) => {
          const post = feed.posts.find((p) => p.event_id === id);
          return (
            <Card
              key={id}
              post={post}
              now={now}
              expanded={expanded === id}
              expand={() => {
                if (!pause) freeze();
                setExpanded(expanded === id ? null : id);
              }}
            />
          );
        })}
        {!visible.length && (
          <div className="slopwatch-empty">
            <h3>
              {connection === "connected"
                ? "No matching posts right now"
                : "Waiting for the live feed"}
            </h3>
            <p>
              {connection === "connected"
                ? "New detections appear here as they arrive. Try another filter or leave the feed running."
                : connection === "unavailable"
                  ? "This browser cannot open the live feed. You can still scan your own text above."
                  : "The feed will reconnect automatically. You can still scan your own text above."}
            </p>
          </div>
        )}
      </div>
      <details className="slopwatch-coverage">
        <summary>Source coverage and scan counts</summary>
        <p>
          Posts are shown from a rolling 15-minute window. The arrival rate uses
          the same window, or the time since this feed started when it has been
          running for less than 15 minutes.
        </p>
        <p>
          Scans count model requests; detections count matching post revisions,
          including reused results for identical text. Display filters above do
          not change these counts. They do not measure the share of AI text
          across a whole platform. The percentage counts classified post
          revisions in the last 15 minutes, including reused results, before
          display exclusions. It includes AI-generated and AI-edited text at low
          confidence or higher, regardless of display filters. Sources without a
          recent update are omitted.
        </p>
        <ul>
          {sources.map((source) => {
            const status = feed.sources[source];
            return (
              <li key={source}>
                <strong>{names[source]}</strong>
                <span>
                  {connection === "connected"
                    ? statusName(status)
                    : "Live status unavailable"}
                </span>
                <small>
                  {source === "reddit"
                    ? "New /r/all self-posts"
                    : source === "mastodon"
                      ? "Configured instances’ public timelines"
                      : source === "lemmy"
                        ? "Posts and comments on configured instances"
                        : "Public Jetstream posts"}
                </small>
                {status?.counter_started_at && status.observed_at ? (
                  <small>
                    {status.counters.scored ?? 0} text scans ·{" "}
                    {status.counters.detected ?? 0} detections ·{" "}
                    {status.counters.withheld ?? 0} withheld from display
                    <br />
                    {status.detection_min_confidence && (
                      <>
                        Generated + edited, {status.detection_min_confidence}{" "}
                        confidence or higher.
                        <br />
                      </>
                    )}
                    Since {new Date(status.counter_started_at).toLocaleString()}
                    ; updated {ago(status.observed_at, now)}.
                  </small>
                ) : (
                  <small>Scan counts unavailable.</small>
                )}
              </li>
            );
          })}
        </ul>
      </details>
      <footer className="slopwatch-footer">
        <p>
          {percentage !== null && (
            <>
              <strong>{percentage}%</strong> of posts we scanned in the last 15
              minutes were AI slop.{" "}
            </>
          )}
          <a href="/pricing" onClick={() => countInteraction("extension")}>
            <strong>Stylome keeps your feed clean.</strong>
          </a>
        </p>
        <a
          href="#scan-text"
          onClick={() => {
            countInteraction("scanner");
            document.getElementById("scan-text")?.focus();
          }}
        >
          Scan your own text ↑
        </a>
      </footer>
    </section>
  );
}

export function Slopwatch() {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/v1/config", { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((config) => {
        if (!controller.signal.aborted)
          setEnabled(config?.slopwatch_enabled === true);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);
  return enabled ? <SlopwatchPanel /> : null;
}
